/* eslint-disable dot-notation */
import React, { useEffect, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import clsx from 'clsx';
import {
  FieldResponse,
  FieldTypeEnumResponse,
  StackReportDataResponse,
  PositionResponse,
  DeductionsResponse,
  FieldWithDeductionsResponse,
  FieldBooleanResponse,
  FieldWithOptionsMultiSelectResponse,
  FieldWithOptionsResponse,
  OptionResponse,
  FieldWithKeyValuePairStringIntResponse,
  FieldWithManualAreaResponse,
  ManualAreaResponse,
} from '.generated/api';

import { convertToSI } from 'helpers/units';
import { useLocation } from 'react-router-dom';
import { Grid, Divider, withStyles } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { apiTranslateToLanguage } from 'translations/translations';
import QRCode from 'react-qr-code';

import logo from 'static/media/forest_x_color.svg';
import bue from 'static/media/bue.png';

import FieldId from 'helpers/FieldId';

import AnnotatedImage from './AnnotatedImage';
import DiameterDistributionChart, {
  DiameterDistributionChartProps,
  labelFromLimit,
  calcDiameterDistributionData,
  BinLimit,
} from './DiameterDistributionChart';
import { ApiService } from 'services/apiService';

import L from 'leaflet';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerIconRetina from 'leaflet/dist/images/marker-icon-2x.png';

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIconRetina,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

const logStackProGrey = '#4e6072';
const certifiedBlue = '#1964c8';
const red = '#d0342c';

const useStyles = makeStyles((theme) => ({
  logo: {
    marginTop: '0cm',
    marginLeft: '1cm',
    maxWidth: '33%',
    marginBottom: '1cm',
  },
  bue: {
    position: 'absolute',
    top: '0cm',
    left: 0,
    width: '100%',
  },
  organizationLogo: {
    position: 'absolute',
    top: '1.4cm',
    right: '1cm',
    maxWidth: '20%',
    maxHeight: '1.6cm',
  },
  headerContainer: {
    paddingLeft: theme.spacing(1),
    marginBottom: '1rem',
  },
  mainTitle: {
    fontSize: '1.0rem',
    color: logStackProGrey,
    fontWeight: 'bold',
  },
  mainValue: {
    fontSize: '1.0rem',
    fontWeight: 'bold',
  },
  tableTitle: {
    color: logStackProGrey,
  },
  tableListItem: {
    marginRight: theme.spacing(1),
  },
  tableBlue: {
    color: certifiedBlue,
  },
  tableBold: {
    fontWeight: 'bold',
  },
  bodyContainer: {
    marginLeft: theme.spacing(1),
  },
  diagramContainer: {
    float: 'right',
  },
  bodySection: {
    '& td': {
      padding: 0,
    },
    marginBottom: theme.spacing(2),
  },
  firstColumn: {
    width: '4.2cm',
  },
  alignColumn: {
    verticalAlign: 'top',
  },
  imageContainer: {
    width: '100%',
    backgroundColor: '#b4b4b4',
    padding: theme.spacing(1),
  },
  imageContainerCertified: {
    backgroundColor: certifiedBlue,
  },
  imageContainerTopCaption: {
    color: logStackProGrey,
  },
  imageContainerTopCaptionCertified: {
    width: 'fit-content',
    color: certifiedBlue,
    backgroundColor: 'black',
  },
  imageContainerBottomCaption: {
    paddingLeft: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  image: {
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: '100%',
    maxHeight: '7cm',
    marginBottom: theme.spacing(1),
  },
  closeupImage: {
    maxWidth: '10cm',
    maxHeight: '7cm',
    marginBottom: theme.spacing(1),
  },
  nobreak: {
    pageBreakInside: 'avoid',
    pageBreakAfter: 'auto',
    pageBreakBefore: 'auto',
  },
  caption: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
  },
  remark: {
    fontWeight: 'bold',
    color: logStackProGrey,
    marginBottom: theme.spacing(1),
  },
  warning: {
    color: red,
    marginLeft: theme.spacing(1),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
  qrCode: {
    textAlign: 'right',
  },
  mapContainer: {
    width: '100%',
    height: '5cm',
  },
}));

const StyledDivider = withStyles((theme) => ({
  root: {
    height: 4,
    backgroundColor: logStackProGrey,
    marginBottom: theme.spacing(1),
  },
}))(Divider);

const axiosRequestConfig = (token: string): AxiosRequestConfig => {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

type Field = {
  label: string;
  value: string | string[];
  certified?: boolean;
  bold?: boolean;
};

type Section = {
  fields: Field[];
};

type Image = {
  src: string;
  annotation?: string;
  certified?: boolean;
  topCaption?: string;
  bottomCaption?: string;
};

type Location = {
  lat: number;
  lng: number;
};

type StackReportData = {
  header?: Field[];
  body?: Section[];
  remark?: { text: string[]; blueIndex?: number };
  footer?: string;
  frontImage?: Image;
  backImage?: Image;
  closeupImage?: Image;
  organizationLogo?: string;
  diameterDistributionData?: DiameterDistributionChartProps;
  location?: Location;
  frontBackWarning?: string;
};

const testStackReportData = {
  header: [
    { label: 'Maßnahmen-Nr', value: '11111' },
    { label: 'Los-Nr', value: '22222' },
    { label: 'Polter-Nr', value: '333333' },
  ],
  body: [
    {
      fields: [
        { label: 'Messdatum', value: '2020-08-06' },
        {
          label: 'Koordinaten',
          value: '59.12346° N, 9.98765° E',
        },
        { label: 'Vermesser', value: 'Jack Lumber' },
        { label: 'Forstamt', value: '44444444' },
        { label: 'Revier', value: '55555555' },
        { label: 'BSA', value: '66666666' },
        {
          label: 'Baumarten',
          value: 'GTA 20% HBU 30% KI 40% KIR 10%',
        },
        {
          label: 'Sorte',
          value: [
            'BC 5%',
            'CGW 10%',
            'C 15%',
            'D 8%',
            'IL 24%',
            'N 16%',
            'F 7%',
            'K 6%',
            'OG 9%',
          ],
        },
      ],
    },

    {
      fields: [
        { label: 'Frontfläche', value: '3.888 m2', certified: true },
        { label: 'Rückfläche', value: '0.000 m2' },
        { label: 'Rm Brutto', value: '11.663 m3' },
        {
          label: 'Rm Netto',
          value: '11.196 m3 (mit 4 % Luftabzug)',
        },
        {
          label: 'Fm Fotovermessung',
          value: '6.402 m3 inkl. Unterlagen (Fak. 0.527)',
        },
        {
          label: 'Fm RVR (Rm netto x UF)',
          value: '6.971 m3 inkl. Unterlagen (Fak. 0.600)',
        },
        {
          label: 'Referenzfläche',
          value: '7.000 m2',
          certified: true,
        },
      ],
    },
    {
      fields: [
        { label: 'Anzahl (+Unterlagen)', value: '49 (+2)', bold: true },
        { label: 'Bestelllänge', value: '5.00 m' },
        { label: 'Polterbreite', value: '30.00 m' },
        {
          label: 'Fm Unterlagen',
          value: '1.001 m3',
        },
        {
          label: 'Mittl. Stückmasse',
          value: '0.501 m3',
        },
        {
          label: 'Mittl. Durchmesser',
          value: '36.0 cm',
        },
      ],
    },
    {
      fields: [
        {
          label: 'Information',
          value:
            'Das Volumen der Unterlagen wird über den Durchschnittsstamm berechnet.',
        },
        {
          label: 'Vermessung-ID',
          value: '12345678-abcd-9876-5432-0987654321ab',
          certified: true,
        },
        {
          label: 'Applikation-ID',
          value: '688b2c4cdf42b44a68d8cfaea8cd81cb9df16c8a',
          certified: true,
        },
        {
          label: 'Kalibrierung-ID',
          value: 'a44b3377afdc3f6e85961c3394911b81ac7eb2c0',
          certified: true,
        },
      ],
    },
  ],
  remark: {
    text: [
      'Anmerkung: ',
      'Messergebnisse und Polygonzüge nach MessEV sind ',
      'blau',
      ' hervorgehoben. Nur diese konformitätsbewerteten Daten sind zu Abrechnungszwecken zu verwenden.',
    ],
    blueIndex: 2,
  },
  frontImage: {
    src: logo,
    certified: true,
    topCaption:
      'Vorderseite | ID: e68b226a-cc5e-426c-8fa0-74739d23d6e0 | Polygonstützpunkte: 250 (8.3 pro meter Polterbreite)',
    bottomCaption:
      'Ermittelte Höhen (mm) des Polygons, beginnend von links bei Sektionslänge (m): 2: 1100 | 4: 2200 | 6: 2500 | 8: 1600 | 10: 400',
  },
  backImage: {
    src: logo,
    topCaption:
      'Rückseite | ID: 80d5d710-d9a8-417c-8d28-c26939c6c2ad | Polygonstützpunkte: 200 (6.5 pro meter Polterbreite)',
    bottomCaption:
      'Ermittelte Höhen (mm) des Polygons, beginnend von links bei Sektionslänge (m): 2: 1000 | 4: 2100 | 6: 2300 | 8: 1700 | 10: 500',
  },
  organizationLogo: logo,
  footer:
    'Heidegesellschaft Forstprodukte und -geräte GmbH | Technologiepark 24 | 22946 Trittau | Tel.: 04154 / 8448 - 55 | www.hdsilva.de',

  diameterDistributionData: {
    countTitle: 'Anzahl je Stärkeklasse',
    volumeTitle: 'Festmeter je Stärkeklasse',
    volumeUnit: 'm3',
    diameterUnit: 'cm',
    distribution: [
      { count: 1, label: '<10', volume: 0.029 },
      { count: 2, label: '10-15', volume: 0.312 },
      { count: 3, label: '15-17', volume: 0.905 },
      { count: 4, label: '17-20', volume: 1.636 },
      { count: 5, label: '20-25', volume: 2.648 },
      { count: 6, label: '25-30', volume: 5.483 },
      { count: 7, label: '30-35', volume: 8.604 },
      { count: 8, label: '35-40', volume: 12.688 },
      { count: 5, label: '40-50', volume: 10.902 },
      { count: 4, label: '50-60', volume: 15.047 },
      { count: 3, label: '60-70', volume: 15.866 },
      { count: 2, label: '70-80', volume: 14.151 },
      { count: 1, label: '>80', volume: 7.729 },
    ],
  },
  location: { lat: 56.1768349, lng: 10.1898794 },
};

function stackReportDataFromResponse(resp?: StackReportDataResponse) {
  if (
    !resp ||
    !resp.measurementCard ||
    !resp.measurementCard.reportConfig ||
    !resp.values
  ) {
    return undefined;
  }
  const measurementCard = resp.measurementCard;
  const fields = measurementCard.fields;
  const values = resp.values;

  const findField = (fieldId: string) =>
    fields.find((field) => field.id === fieldId);

  const useFullMeasurementReportLayout =
    values[FieldId.fullMeasurement] === true;

  const reportConfig = measurementCard.reportConfig;

  const reportLayout = useFullMeasurementReportLayout
    ? reportConfig.adminReportLayoutForFullMeasurement
    : reportConfig.adminReportLayoutForLogCount;

  if (!reportLayout) {
    return undefined;
  }

  const showCertificateInfo =
    useFullMeasurementReportLayout && reportConfig.showCertificateInfo === true;

  const boldFields: Record<string, boolean> = {
    [FieldId.netVolume]: true,
    [FieldId.logsTotal]: true,
    [FieldId.totalRaummeter]: true,
  };

  const mapField = (fieldId: string, certified: boolean): Field => {
    const field = measurementCard.fields.find((field) => field.id === fieldId);
    if (!field) {
      return {
        label: '',
        value: '',
      };
    }
    const value = values[fieldId];
    return {
      label: field
        ? apiTranslateToLanguage(field.title, reportConfig.language)
        : '',
      value:
        value !== undefined && value !== null
          ? fieldValueToString(field, value, reportConfig.language)
          : '',
      certified: certified,
      bold: boldFields[fieldId] === true,
    };
  };

  const calcDiameterDistribution = () => {
    // fetch all relevant units
    const logDiametersUnit = fields.find(
      (field) => field.id === FieldId.logDiameters,
    )?.units;
    const logDiameterAvgUnit = fields.find(
      (field) => field.id === FieldId.logDiameterAvg,
    )?.units;

    const fieldIdForDiameterDistributionVolume = measurementCard.reportConfig
      .diameterDistributionVolume
      ? measurementCard.reportConfig.diameterDistributionVolume
      : FieldId.netVolume;

    const volumeForDiameterDistributionUnit = fields.find(
      (field) => field.id === fieldIdForDiameterDistributionVolume,
    )?.units;
    const logLengthUnit = fields.find(
      (field) => field.id === FieldId.logLength,
    )?.units;

    if (
      !(
        logDiametersUnit?.type &&
        logDiameterAvgUnit?.type &&
        volumeForDiameterDistributionUnit?.type &&
        reportConfig.diameterUnits?.type
      )
    ) {
      console.error('Missing units');
      return undefined;
    }

    const logLength = logLengthUnit
      ? convertToSI(logLengthUnit.type, values[FieldId.logLength])
      : 0;

    const volumeForDiameterDistribution = convertToSI(
      volumeForDiameterDistributionUnit.type,
      values[fieldIdForDiameterDistributionVolume],
    );

    const volumeForDiameterDistributionUnitStr: string = apiTranslateToLanguage(
      volumeForDiameterDistributionUnit.display ?? {},
      reportConfig.language,
    );

    const logDiameters: number[] = values[FieldId.logDiameters]?.map(
      (diameter: number) => convertToSI(logDiametersUnit.type, diameter),
    );

    const logDiameterAvg = convertToSI(
      logDiameterAvgUnit.type,
      values[FieldId.logDiameterAvg],
    );

    const joistCount: number = values[FieldId.joistCount];
    const binLimits: BinLimit[] =
      reportConfig.diameterDistributionIntervals.map(({ min, max }) => ({
        label: labelFromLimit(min, max),
        min: convertToSI(reportConfig.diameterUnits.type, min),
        max: convertToSI(reportConfig.diameterUnits.type, max),
      }));

    const diameterUnits: string = apiTranslateToLanguage(
      reportConfig?.diameterUnits?.display ?? {},
      reportConfig.language,
    );

    if (
      logLength !== undefined &&
      volumeForDiameterDistribution !== undefined &&
      logDiameters &&
      binLimits
    ) {
      if (logDiameterAvg && joistCount) {
        for (let i = 0; i < joistCount; ++i) {
          logDiameters.push(logDiameterAvg);
        }
      }

      return {
        countTitle: reportConfig.diameterDistributionNumberPrStrengthClass,
        volumeTitle: reportConfig.diameterDistributionCubicMeterPrStrengthClass,
        volumeUnit: volumeForDiameterDistributionUnitStr,
        diameterUnit: diameterUnits,
        distribution: calcDiameterDistributionData(
          binLimits,
          logDiameters,
          logLength,
          volumeForDiameterDistribution,
          volumeForDiameterDistributionUnit.type,
        ),
      };
    }
    return undefined;
  };

  const topCaption = (
    uuid?: string,
    areaPolygonPts?: number,
    stackLength?: number,
  ) => {
    if (useFullMeasurementReportLayout) {
      return `${reportConfig.frontTitle} | ID: ${uuid ?? ''} | ${
        reportConfig.polygonBasesTitle
      } ${areaPolygonPts ?? ''} | ${reportConfig.perMeterOfPileTitle}: ${
        areaPolygonPts && stackLength
          ? (areaPolygonPts / stackLength).toFixed(2)
          : ''
      }`;
    }

    return `${reportConfig.frontTitle} | ID: ${uuid ?? ''}`;
  };

  const topCaptionBack = (
    uuid?: string,
    areaPolygonPts?: number,
    stackLength?: number,
  ) => {
    if (useFullMeasurementReportLayout) {
      return `${reportConfig.backTitle} | ID: ${uuid ?? ''} | ${
        reportConfig.polygonBasesTitle
      } ${areaPolygonPts ?? ''} | ${reportConfig.perMeterOfPileTitle}: ${
        areaPolygonPts && stackLength
          ? (areaPolygonPts / stackLength).toFixed(2)
          : ''
      }`;
    }

    return `${reportConfig.backTitle} | ID: ${uuid ?? ''}`;
  };

  const topCloseupCaption = `${reportConfig.closeupTitle}`;

  const bottomCaption = (
    heights?: number[],
    heightUnit?: string,
    interval?: number,
    intervalUnit?: string,
  ) => {
    return `${reportConfig.determinedHeightOfPolygonTitle} ${
      heights
        ? heights
            .map((height) => `${height.toFixed(1)}${heightUnit ?? ''}`)
            .join(', ')
        : ''
    } ${interval ? `(⇤ ${interval.toFixed(1)}${intervalUnit ?? ''} ⇥)` : ''}`;
  };

  const remark = () => {
    const text = [];
    let blueIndex;
    if (reportConfig.remarkTitle) {
      text.push(reportConfig.remarkTitle);
      if (reportConfig.remarkValue) {
        if (reportConfig.remarkBlueIndex && reportConfig.remarkBlueLength) {
          const remarkAfterBlueIndex =
            reportConfig.remarkBlueIndex + reportConfig.remarkBlueLength;

          text.push(
            reportConfig.remarkValue.substring(0, reportConfig.remarkBlueIndex),
          );

          blueIndex = text.length;

          text.push(
            reportConfig.remarkValue.substring(
              reportConfig.remarkBlueIndex,
              remarkAfterBlueIndex,
            ),
          );

          text.push(reportConfig.remarkValue.substring(remarkAfterBlueIndex));
        } else {
          text.push(reportConfig.remarkValue);
        }
      }
    }

    return {
      text,
      blueIndex,
    };
  };

  const frontImageCertified =
    values[FieldId.frontImageCertified] === true && showCertificateInfo;
  const backImageCertified =
    values[FieldId.backImageCertified] === true && showCertificateInfo;

  // certified field map. If front image is certified a predefined set of field ids will be added to this map. Likewise with back image.
  const certifiedFields: Record<string, boolean> = {};

  if (frontImageCertified) {
    certifiedFields[FieldId.frontArea] = true;
    certifiedFields[FieldId.referenceArea] = true;
    certifiedFields[FieldId.stackUUID] = true;
    certifiedFields[FieldId.applicationID] = true;
    certifiedFields[FieldId.calibrationID] = true;
  }

  if (backImageCertified) {
    certifiedFields[FieldId.backArea] = true;
    certifiedFields[FieldId.stackUUID] = true;
    certifiedFields[FieldId.applicationID] = true;
    certifiedFields[FieldId.calibrationID] = true;
  }

  const reportData: StackReportData = {};
  if (reportLayout.reportHeaderFields) {
    reportData.header = reportLayout.reportHeaderFields.map((fieldId) =>
      mapField(fieldId, certifiedFields[fieldId] === true),
    );
  }

  if (reportLayout.reportBodySections) {
    reportData.body = reportLayout.reportBodySections.map((section) => ({
      fields: section.fields.map((fieldId) =>
        mapField(fieldId, certifiedFields[fieldId] === true),
      ),
    }));
  }

  if (
    reportConfig.frontBackWarning &&
    (values[FieldId.backArea] || values[FieldId.manualBackArea]) &&
    values[FieldId.frontArea]
  ) {
    if (
      isDifferenceGreaterThan15Percent(
        values[FieldId.backArea] || values[FieldId.manualBackArea],
        values[FieldId.frontArea],
      )
    ) {
      reportData.frontBackWarning = reportConfig.frontBackWarning;
    }
  }

  if (resp.frontImage) {
    const frontHeights = values[FieldId.frontHeights];
    const frontHeightUnit = apiTranslateToLanguage(
      findField(FieldId.frontHeights)?.units?.display,
      reportConfig.language,
    );
    const frontInterval = values[FieldId.frontInterval];
    const frontIntervalUnit = apiTranslateToLanguage(
      findField(FieldId.frontInterval)?.units?.display,
      reportConfig.language,
    );
    const frontUUID = values[FieldId.frontUUID];
    const frontAreaPolygonPts = values[FieldId.frontAreaPolygonPts];
    const stackLength = values[FieldId.stackLength];

    reportData.frontImage = {
      src: resp.frontImage,
      annotation: resp.frontImageOverlay ?? undefined,
      certified: frontImageCertified,
      topCaption: topCaption(frontUUID, frontAreaPolygonPts, stackLength),
      bottomCaption: useFullMeasurementReportLayout
        ? bottomCaption(
            frontHeights,
            frontHeightUnit,
            frontInterval,
            frontIntervalUnit,
          )
        : undefined,
    };
  }

  if (resp.backImage) {
    const backHeights = values[FieldId.backHeights];
    const backHeightUnit = apiTranslateToLanguage(
      findField(FieldId.backHeights)?.units?.display,
      reportConfig.language,
    );
    const backInterval = values[FieldId.backInterval];
    const backIntervalUnit = apiTranslateToLanguage(
      findField(FieldId.backInterval)?.units?.display,
      reportConfig.language,
    );
    const backUUID = values[FieldId.backUUID];
    const backAreaPolygonPts = values[FieldId.backAreaPolygonPts];
    const stackLength = values[FieldId.stackLength];

    reportData.backImage = {
      src: resp.backImage,
      annotation: resp.backImageOverlay ?? undefined,
      certified: backImageCertified,
      topCaption: topCaptionBack(backUUID, backAreaPolygonPts, stackLength),
      bottomCaption: useFullMeasurementReportLayout
        ? bottomCaption(
            backHeights,
            backHeightUnit,
            backInterval,
            backIntervalUnit,
          )
        : undefined,
    };
  }

  if (resp.closeupImage) {
    reportData.closeupImage = {
      src: resp.closeupImage,
      annotation: resp.closeupImageOverlay ?? undefined,
      topCaption: topCloseupCaption,
    };
  }

  if (resp.organizationLogoImage) {
    reportData.organizationLogo = resp.organizationLogoImage;
  }

  const calibrationId =
    values[FieldId.calibrationID] !== undefined &&
    values[FieldId.calibrationID] !== null &&
    values[FieldId.calibrationID] !== '';

  if (showCertificateInfo && calibrationId) {
    reportData.remark = remark();
  }
  reportData.footer = reportLayout.reportFooter ?? '';
  if (useFullMeasurementReportLayout) {
    reportData.diameterDistributionData = calcDiameterDistribution();
  }

  const locationMeasurement = values[FieldId.locationMeasurement];
  if (
    locationMeasurement &&
    locationMeasurement.latitude &&
    locationMeasurement.longitude
  ) {
    reportData.location = {
      lat: locationMeasurement.latitude,
      lng: locationMeasurement.longitude,
    };
  }

  return reportData;
}

function formatList(items: string[]): string {
  return items.join(' ');
}

function formatDouble(value: number, unit: string): string {
  return `${value.toFixed(3)} ${unit}`;
}

function formatDoubleArray(values: number[], unit: string): string {
  return formatList(values.map((value) => formatDouble(value, unit)));
}

function formatIntArray(values: number[], unit: string): string {
  return formatList(values.map((value) => formatInteger(value, unit)));
}

function formatDate(v: string, language: string): string {
  const date = new Date(v);
  return date ? date.toLocaleDateString(language) : '';
}

function formatInteger(v: number, unit: string): string {
  return `${v} ${unit}`;
}

function formatMultipleOptions(
  selectedKeys: string[],
  options: OptionResponse[],
  language: string,
): string {
  const values = options
    .filter((option) => selectedKeys.includes(option.key))
    .map((option) => apiTranslateToLanguage(option.text, language));
  return formatList(values);
}

function formatString(v: string): string {
  return v;
}

function formatText(v: string): string {
  return v;
}

function formatKeyValuePairStringInt(
  v: Record<string, number>,
  options: OptionResponse[],
  unit: string,
  language: string,
): string[] {
  return Object.keys(v).map((key) => {
    const option = options.find((option) => option.key === key);
    const translatedValue = option
      ? apiTranslateToLanguage(option.text, language)
      : '';
    return `${translatedValue} ${formatInteger(v[key], unit)}`;
  });
}

function formatPosition({ latitude, longitude }: PositionResponse): string {
  const fixedLat = Math.abs(latitude).toFixed(7);
  const fixedLng = Math.abs(longitude).toFixed(7);
  const ns = latitude < 0 ? 'S' : 'N';
  const ew = longitude < 0 ? 'W' : 'E';
  return `${fixedLat}° ${ns}, ${fixedLng}° ${ew}`;
}

function formatDeductions(
  { deductionsCount, averageCount }: DeductionsResponse,
  keyUnit: string,
): string[] {
  const result: string[] = [];
  if (averageCount) {
    result.push(`Ø: ${averageCount}`);
  }
  if (deductionsCount) {
    for (const { item1, item2 } of deductionsCount) {
      result.push(`${item1} ${keyUnit}: ${item2}`);
    }
  }
  return result;
}

function formatManualArea(
  {
    sectionAHeights,
    sectionALength,
    sectionBHeights,
    sectionBLength,
  }: ManualAreaResponse,
  AHeightsTitle: string,
  ALengthTitle: string,
  BHeightTitle: string,
  BLengthTitle: string,
): string[] {
  const result: string[] = [];
  if (ALengthTitle) {
    result.push(`${ALengthTitle}: ${sectionALength} m`);
  }
  if (sectionAHeights) {
    result.push(`${AHeightsTitle}: ${sectionAHeights.join(', ')} m`);
  }
  if (BLengthTitle) {
    result.push(`${BLengthTitle}: ${sectionBLength} m`);
  }
  if (BHeightTitle) {
    result.push(`${BHeightTitle}: ${sectionBHeights} m`);
  }
  return result;
}

function formatBoolean(
  value: boolean,
  { trueLabel, falseLabel }: FieldBooleanResponse,
  language: string,
) {
  return value === true
    ? apiTranslateToLanguage(trueLabel ?? {}, language)
    : value === false
    ? apiTranslateToLanguage(falseLabel ?? {}, language)
    : '';
}

function fieldValueToString(
  field: FieldResponse,
  value: unknown,
  language: string,
) {
  let formattedValue: string | string[] = '';
  const unit = field.units?.display
    ? apiTranslateToLanguage(field.units.display, language)
    : '';
  switch (field.fieldType) {
    case FieldTypeEnumResponse.DOUBLE:
      formattedValue = formatDouble(value as number, unit);
      break;
    case FieldTypeEnumResponse.ARRAY_OF_DOUBLE:
      formattedValue = formatDoubleArray(value as number[], unit);
      break;
    case FieldTypeEnumResponse.ARRAY_OF_INT:
      formattedValue = formatIntArray(value as number[], unit);
      break;

    case FieldTypeEnumResponse.DATE:
      formattedValue = formatDate(value as string, language);
      break;
    case FieldTypeEnumResponse.INTEGER:
      formattedValue = formatInteger(value as number, unit);
      break;
    case FieldTypeEnumResponse.MULTIPLE_OPTIONS: {
      const optionsMultiField = field as FieldWithOptionsMultiSelectResponse;
      formattedValue = formatMultipleOptions(
        value as string[],
        optionsMultiField.options ?? [],
        language,
      );
      break;
    }
    case FieldTypeEnumResponse.STRING:
      formattedValue = formatString(value as string);
      break;
    case FieldTypeEnumResponse.TEXT:
      formattedValue = formatText(value as string);
      break;
    case FieldTypeEnumResponse.OPTIONS: {
      const optionsField = field as FieldWithOptionsResponse;
      const option = optionsField.options?.find(
        (option) => option.key === value,
      );
      const translatedValue = option
        ? apiTranslateToLanguage(option.text, language)
        : '';
      formattedValue = formatString(translatedValue);
      break;
    }
    case FieldTypeEnumResponse.KEY_VALUE_PAIR_STRING_INT: {
      const keyValuePairStringIntField =
        field as FieldWithKeyValuePairStringIntResponse;
      formattedValue = formatKeyValuePairStringInt(
        value as Record<string, number>,
        keyValuePairStringIntField.options ?? [],
        unit,
        language,
      );
      break;
    }
    case FieldTypeEnumResponse.POSITION:
      formattedValue = formatPosition(value as PositionResponse);
      break;
    case FieldTypeEnumResponse.DEDUCTIONS: {
      const deductionsField = field as FieldWithDeductionsResponse;
      const keyUnit = deductionsField.keyUnit?.display
        ? apiTranslateToLanguage(deductionsField.keyUnit.display, language)
        : '';
      formattedValue = formatDeductions(value as DeductionsResponse, keyUnit);
      break;
    }
    case FieldTypeEnumResponse.BOOLEAN:
      formattedValue = formatBoolean(
        value as boolean,
        field as FieldBooleanResponse,
        language,
      );
      break;
    case FieldTypeEnumResponse.MANUAL_AREA: {
      const manualAreaField = field as FieldWithManualAreaResponse;
      const AHeigthsTitle = apiTranslateToLanguage(
        manualAreaField.sectionAHeightsTitle,
        language,
      );
      const ALengthTitle = apiTranslateToLanguage(
        manualAreaField.sectionALengthTitle,
        language,
      );
      const BHeightTitle = apiTranslateToLanguage(
        manualAreaField.sectionBHeightsTitle,
        language,
      );
      const BLengthTitle = apiTranslateToLanguage(
        manualAreaField.sectionBLengthTitle,
        language,
      );

      formattedValue = formatManualArea(
        value as ManualAreaResponse,
        AHeigthsTitle,
        ALengthTitle,
        BHeightTitle,
        BLengthTitle,
      );
      break;
    }
    // unhandled types
    case FieldTypeEnumResponse.IMAGE:
      formattedValue = 'Unhandled type: ' + field.fieldType;
      break;
    default:
      // TODO: find alternative way to signal this error?
      formattedValue = 'Unexpected type: ' + field.fieldType;
      break;
  }

  return formattedValue;
}

/**
 * To calculate if front and back measurements have a difference greater than 15%
 */
function isDifferenceGreaterThan15Percent(
  frontValue: number,
  backValue: number,
): boolean {
  const absoluteDifference: number = Math.abs(frontValue - backValue);

  const average: number = (frontValue + backValue) / 2;

  const percentageDifference: number = Math.abs(
    (absoluteDifference / average) * 100,
  );

  return percentageDifference > 15;
}

type ImageContainerProps = {
  image: Image;
};
function ImageContainer({ image }: ImageContainerProps) {
  const classes = useStyles();
  return (
    <>
      <Grid
        className={clsx(
          classes.nobreak,
          classes.imageContainer,
          image.certified === true ? classes.imageContainerCertified : null,
        )}
        container
        direction="column"
      >
        {image.topCaption && (
          <div
            className={
              image.certified
                ? classes.imageContainerTopCaptionCertified
                : classes.imageContainerTopCaption
            }
          >
            {image.topCaption}
          </div>
        )}
        <AnnotatedImage
          src={image.src}
          annotation={image.annotation}
          className={classes.image}
        />
      </Grid>
      {image.bottomCaption && (
        <div className={classes.imageContainerBottomCaption}>
          {image.bottomCaption}
        </div>
      )}
    </>
  );
}

function StackMeasurementReport(): React.ReactElement {
  const classes = useStyles();

  const [stackReportData, setStackReportData] = useState<StackReportData>();

  const location = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    const stackId = query.get('stack-id');
    const token = query.get('token');

    if (!stackId || !token) {
      // display with test data
      setStackReportData(testStackReportData);
      return;
    }

    axios
      .get<StackReportDataResponse>(
        `${ApiService.baseUrl}/stack/${stackId}/report-data`,
        axiosRequestConfig(token),
      )
      .then((resp) => {
        // console.debug('stack report data', resp.data);
        setStackReportData(stackReportDataFromResponse(resp.data));
      });
  }, []);

  if (!stackReportData) {
    return (
      <>
        <div>No stack data available!</div>
      </>
    );
  }

  return (
    <>
      <header>
        <img src={logo} className={classes.logo} />
        <img src={bue} className={classes.bue} />
      </header>

      <main>
        <img
          src={stackReportData.organizationLogo}
          className={classes.organizationLogo}
        />
        <Grid
          container
          justify="space-between"
          className={classes.headerContainer}
        >
          {stackReportData.header?.map((header) => (
            <span key={header.label}>
              <span className={classes.mainTitle}>{header.label}:</span>{' '}
              <span className={classes.mainValue}>{header.value}</span>
            </span>
          ))}
        </Grid>
        <Grid container className={classes.bodyContainer}>
          <Grid item xs={12}>
            <div className={classes.diagramContainer}>
              {stackReportData.diameterDistributionData && (
                <DiameterDistributionChart
                  {...stackReportData.diameterDistributionData}
                />
              )}
            </div>
            {stackReportData.body?.map((section, i) => (
              <table key={i} className={classes.bodySection}>
                <tbody>
                  {section.fields.map(
                    ({ label, value, certified, bold }, i) => (
                      <tr key={i}>
                        <td
                          className={clsx(
                            classes.firstColumn,
                            classes.alignColumn,
                          )}
                        >
                          <span
                            className={clsx(
                              classes.tableTitle,
                              certified === true ? classes.tableBlue : null,
                              bold === true ? classes.tableBold : null,
                            )}
                          >
                            {label}
                          </span>
                        </td>
                        <td className={classes.alignColumn}>
                          <span
                            className={clsx(
                              certified === true ? classes.tableBlue : null,
                              bold === true ? classes.tableBold : null,
                            )}
                          >
                            {Array.isArray(value)
                              ? value.map((item, i) => (
                                  <div
                                    key={i}
                                    className={classes.tableListItem}
                                  >
                                    {item}
                                  </div>
                                ))
                              : value}
                          </span>
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            ))}
          </Grid>
          <Grid item xs={5}></Grid>
        </Grid>
        {stackReportData.frontBackWarning && (
          <div className={classes.warning}>
            {stackReportData.frontBackWarning}
          </div>
        )}
        <div className={classes.nobreak}>
          <StyledDivider />
          {stackReportData.remark && (
            <div className={classes.remark}>
              {stackReportData.remark.text.map((text, i) => {
                return i === stackReportData.remark?.blueIndex ? (
                  <span key={i} className={classes.tableBlue}>
                    {text}
                  </span>
                ) : (
                  text
                );
              })}
            </div>
          )}
          {stackReportData.frontImage && (
            <ImageContainer image={stackReportData.frontImage} />
          )}
        </div>
        {stackReportData.backImage && (
          <ImageContainer image={stackReportData.backImage} />
        )}

        {stackReportData.closeupImage && (
          <div className={classes.nobreak}>
            <StyledDivider />
            <div className={classes.imageContainerBottomCaption}>
              {stackReportData.closeupImage.topCaption}
            </div>
            <AnnotatedImage
              src={stackReportData.closeupImage.src}
              annotation={stackReportData.closeupImage.annotation}
              className={classes.image}
            />
          </div>
        )}
        {stackReportData.location && (
          <div className={classes.nobreak}>
            <StyledDivider />
            <Grid
              container
              direction="row"
              justify="flex-start"
              spacing={1}
              className={classes.nobreak}
            >
              <Grid item xs={4}>
                <MapContainer
                  className={classes.mapContainer}
                  bounds={new L.LatLng(
                    stackReportData.location.lat,
                    stackReportData.location.lng,
                  ).toBounds(5000)}
                  zoomControl={false}
                  attributionControl={false}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                    maxZoom={20}
                    subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                  />
                  <Marker
                    position={
                      new L.LatLng(
                        stackReportData.location.lat,
                        stackReportData.location.lng,
                      )
                    }
                  ></Marker>
                </MapContainer>
              </Grid>
              <Grid item xs={6}>
                <MapContainer
                  className={classes.mapContainer}
                  bounds={new L.LatLng(
                    stackReportData.location.lat,
                    stackReportData.location.lng,
                  ).toBounds(1000)}
                  zoomControl={false}
                  scrollWheelZoom={false}
                  attributionControl={false}
                >
                  <TileLayer
                    url="https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                    maxZoom={20}
                    subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                  />
                  <Marker
                    position={
                      new L.LatLng(
                        stackReportData.location.lat,
                        stackReportData.location.lng,
                      )
                    }
                  ></Marker>
                </MapContainer>
              </Grid>
              <Grid item xs={2}>
                <Grid container direction="column" alignItems="center">
                  <a
                    href={`https://maps.google.com/?q=${stackReportData.location.lat},${stackReportData.location.lng}`}
                  >
                    <QRCode
                      size={100}
                      value={`https://maps.google.com/?q=${stackReportData.location.lat},${stackReportData.location.lng}`}
                    />
                    Position in google maps
                  </a>
                </Grid>
                <Grid container direction="column" alignItems="center">
                  <a
                    href={`https://www.topo-gps.com/add/point?lat=${stackReportData.location.lat}&lon=${stackReportData.location.lng}&title=Navigate&description=Topo`}
                  >
                    <QRCode
                      size={100}
                      value={`https://www.topo-gps.com/add/point?lat=${stackReportData.location.lat}&lon=${stackReportData.location.lng}&title=Navigate&description=Topo`}
                    />
                    Position in Topo GPS
                  </a>
                </Grid>
              </Grid>
            </Grid>
          </div>
        )}
      </main>
      {stackReportData.footer && <footer>{stackReportData.footer}</footer>}
    </>
  );
}

export default StackMeasurementReport;
